import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { getPartnerApplication, setPartnerStatus, savePartnerNotes, syncPartner, errText } from "../lib/api";

const STATUSES = ["Submitted", "In Review", "Approved", "Declined", "On Hold"];

const Row = ({ k, v }) => (
  <div className="grid gap-1 border-b border-zinc-900 py-3 sm:grid-cols-[38%_1fr] sm:gap-4">
    <dt className="text-sm text-zinc-500">{k}</dt>
    <dd className="break-words text-sm text-zinc-200">{v || "—"}</dd>
  </div>
);

const Block = ({ title, testid, children }) => (
  <section className="mt-10" data-testid={testid}>
    <h2 className="font-mono-tech border-b border-zinc-800 pb-2 text-[11px] text-brand-indigo">{title}</h2>
    <dl>{children}</dl>
  </section>
);

const list = (v) => (Array.isArray(v) && v.length ? v.join(", ") : "");

export default function PartnerDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getPartnerApplication(id).then((d) => { setP(d); setNotes(d.internal_notes || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!p) return <p className="font-mono-tech p-10 text-[11px] text-zinc-500">Loading…</p>;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(msg); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const c = p.contact || {}, b = p.business || {}, s = p.services || {}, part = p.participation || {};

  return (
    <div className="p-6 md:p-10">
      <Link to="/control/providers" data-testid="back-to-partners" className="font-mono-tech flex items-center gap-2 text-[10px] text-zinc-500 hover:text-zinc-200">
        <ArrowLeft className="h-3 w-3" /> All provider applications
      </Link>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-zinc-50" data-testid="partner-name">{p.display_name}</h1>
          <p className="font-mono-tech mt-2 text-[10px] text-zinc-500">
            {c.email} · {c.phone} · submitted {p.created_at?.slice(0, 10)}
          </p>
        </div>
        <Button data-testid="partner-sync-btn" disabled={busy} onClick={() => act(() => syncPartner(id), "Synced to Notion")}
          className="h-11 rounded-full border border-zinc-800 bg-transparent px-5 text-sm text-zinc-200 hover:border-brand-indigo/60 hover:text-brand-indigo-soft">
          <RefreshCw className="mr-2 h-4 w-4" /> Sync to Notion
        </Button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="partner-status-controls">
        {STATUSES.map((st) => (
          <button key={st} data-testid={`partner-status-${st}`} disabled={busy}
            onClick={() => act(() => setPartnerStatus(id, st), `Status set to ${st}`)}
            className={`font-mono-tech rounded-full border px-4 py-2 text-[10px] transition-colors duration-200 ${
              p.status === st ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
            {st}
          </button>
        ))}
      </div>

      {p.notion?.last_error && (
        <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="partner-notion-error">
          Notion: {p.notion.last_error}
        </p>
      )}

      <div className="md:grid md:grid-cols-[1fr_320px] md:gap-10">
        <div className="min-w-0">
          <Block title="Contact" testid="block-contact">
            <Row k="Name" v={`${c.first_name || ""} ${c.last_name || ""}`.trim()} />
            <Row k="Email" v={c.email} />
            <Row k="Phone" v={c.phone} />
            <Row k="Location" v={[c.city, c.state_province, c.country].filter(Boolean).join(", ")} />
            <Row k="Describes themselves as" v={list(c.applicant_types)} />
            <Row k="Other description" v={c.applicant_types_other} />
          </Block>

          <Block title="Business / professional identity" testid="block-business">
            <Row k="Operates under a business" v={b.has_business} />
            <Row k="Organization" v={b.organization_name} />
            <Row k="Role" v={b.role} />
            <Row k="What they provide" v={b.what_you_do} />
          </Block>

          <Block title="Services" testid="block-services">
            <Row k="Categories" v={list(s.categories)} />
            <Row k="Services" v={list(s.selected)} />
            <Row k="Other service" v={s.other} />
            {Object.entries(s.details || {}).map(([k, v]) => (
              <Row key={k} k={k.replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase())} v={Array.isArray(v) ? v.join(", ") : String(v)} />
            ))}
          </Block>

          <Block title="Rates & pricing" testid="block-rates">
            {(p.rates || []).length === 0 && <Row k="Pricing" v="" />}
            {(p.rates || []).map((r) => (
              <Row key={r.service} k={r.service} v={[r.pricing_model, r.rate, r.notes].filter(Boolean).join(" · ")} />
            ))}
          </Block>

          <Block title="Online presence & portfolio" testid="block-presence">
            <Row k="Website" v={p.presence?.website} />
            <Row k="Socials" v={(p.presence?.socials || []).map((x) => `${x.platform}: ${x.url}`).join(" | ")} />
            <Row k="Portfolio links" v={list(p.portfolio?.links)} />
            <Row k="Portfolio notes" v={p.portfolio?.notes} />
          </Block>

          <Block title="Participation" testid="block-participation">
            <Row k="Interests" v={list(part.interests)} />
            <Row k="Other interest" v={part.interests_other} />
            <Row k="Ideal fit" v={part.ideal_fit} />
            <Row k="Work style" v={list(part.work_style)} />
            <Row k="Service reach" v={list(part.service_reach)} />
            <Row k="Primary service area" v={part.primary_service_area} />
          </Block>

          <Block title="Experience & referral" testid="block-experience">
            <Row k="Years doing this" v={p.experience?.years} />
            <Row k="Notes" v={p.experience?.notes} />
            <Row k="Heard about Sabrea via" v={list(p.referral?.sources)} />
            <Row k="Referred by" v={p.referral?.referred_by} />
          </Block>
        </div>

        <aside className="mt-10 space-y-6 md:mt-10">
          <div className="rounded-xl border border-brand-indigo/30 bg-brand-indigo/5 p-5" data-testid="partner-petition">
            <p className="font-mono-tech flex items-center gap-2 text-[10px] text-brand-indigo"><ShieldCheck className="h-3.5 w-3.5" /> Petition signed</p>
            <p className="mt-3 font-display text-lg text-zinc-100">{p.petition?.signature}</p>
            <p className="font-mono-tech mt-1 text-[10px] text-zinc-500">
              {p.petition?.signed_date} · v{p.petition?.version}
            </p>
            <p className="font-mono-tech mt-3 text-[10px] text-zinc-600">Server: {p.petition?.server_timestamp}</p>
            {p.petition?.ip && <p className="font-mono-tech mt-1 text-[10px] text-zinc-600">IP: {p.petition.ip}</p>}
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="partner-notes">
            <p className="font-mono-tech text-[10px] text-zinc-500">Internal notes</p>
            <Textarea data-testid="partner-notes-input" rows={7} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-3 rounded-xl border-zinc-800 bg-zinc-900/70 text-zinc-100" placeholder="Only the team sees this." />
            <Button data-testid="partner-notes-save" disabled={busy} onClick={() => act(() => savePartnerNotes(id, notes), "Notes saved")}
              className="mt-3 h-11 w-full rounded-full bg-brand-indigo text-sm font-semibold text-white hover:bg-brand-indigo-soft">
              <Save className="mr-2 h-4 w-4" /> Save notes
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
